"use client";

import { ReactNode } from "react";
import { cn } from "@drpcrd/utils/shared";
import { CardWrapper } from "../card-wrapper";
import { CreatorAvatar } from "./creator-avatar";
import { Typography } from "../typography";

export type CreatorMatchCardSocial = {
  id: string | number;
  username: string;
  icon: ReactNode;
  profileUrl: string;
  rateLabel?: string;
  metricLabel?: string;
  metricValue?: string;
};

type Props = {
  name: string;
  subtitle?: string;
  avatarSrc?: string | null;
  social1Src?: string | null;
  social2Src?: string | null;
  countryCode?: string | null;
  showCountryFlag?: boolean;
  socials?: CreatorMatchCardSocial[];
  /** Rendered top-right over the avatar, e.g. save/shortlist buttons. */
  actions?: ReactNode;
  /** Rendered under the subtitle — e.g. brand's "4 notes" pill. Pass a
   * taller height via `className` to make room for it. */
  badge?: ReactNode;
  onClick?: () => void;
  className?: string;
};

/**
 * Creator match card — avatar, name, subtitle and per-platform social rows
 * with rate/metric. Extracted from the brand app's creator-match card
 * (apps/brand/.../creator-card) so it can be reused across dashboards that
 * show creators moving through discovery/roster flows without the
 * Algolia-hit/campaign-specific data model.
 */
export const CreatorMatchCard = ({
  name,
  subtitle,
  avatarSrc,
  social1Src,
  social2Src,
  countryCode,
  showCountryFlag = false,
  socials = [],
  actions,
  badge,
  onClick,
  className,
}: Props) => (
  <CardWrapper
    className={cn("h-33 sm:h-43", onClick && "hover:cursor-pointer", className)}
    onClick={onClick}
  >
    <div className="group flex h-full w-full flex-col justify-between px-3 pt-3">
      <div className="mb-1.5 flex w-full flex-row items-start justify-between sm:mb-1">
        <CreatorAvatar
          size="lg"
          creatorSrc={avatarSrc}
          social1Src={social1Src}
          social2Src={social2Src}
          className="h-4 w-4 sm:h-6 sm:w-6"
          countryCode={countryCode}
          showCountryFlag={showCountryFlag}
          flagClassName="absolute -bottom-1 -right-1 leading-3 text-lg flex items-center justify-center"
        />
        {actions && <div className="flex justify-center gap-2">{actions}</div>}
      </div>
      <div className="flex flex-col gap-1.5 sm:gap-1">
        <Typography
          weight="semibold"
          className="text-sm leading-2.75 sm:text-lg sm:leading-3"
        >
          {name}
        </Typography>
        {subtitle && (
          <Typography
            className="max-w-34 overflow-hidden text-ellipsis whitespace-nowrap"
            size="sm"
            color="neutral"
          >
            {subtitle}
          </Typography>
        )}
        {badge && <div className="mt-1 flex">{badge}</div>}
      </div>
    </div>
    <div className="mx-3 mb-3 flex flex-col">
      {socials.map((social) => (
        <div key={social.id}>
          <div className="mt-1 flex flex-row items-center justify-between gap-1">
            <span className="h-2 w-2 shrink-0 [&>svg]:h-2 [&>svg]:w-2">
              {social.icon}
            </span>
            <a
              href={social.profileUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              title={social.username}
              className="hover:text-primary flex-1 overflow-x-hidden text-sm font-semibold text-ellipsis transition-colors hover:cursor-pointer"
            >
              {social.username}
            </a>
            {social.rateLabel && (
              <Typography size="sm">{social.rateLabel}</Typography>
            )}
          </div>
          {social.metricValue && (
            <div className="pl-3 text-sm text-neutral-900">
              {social.metricLabel} {social.metricValue}
            </div>
          )}
        </div>
      ))}
    </div>
  </CardWrapper>
);
